import React, { useState, useEffect, useMemo } from 'react';
import { Search, Download, RefreshCw, AlertTriangle, X, CheckCircle, List, Check } from 'lucide-react';
import {
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Button,
  Checkbox,
  CircularProgress
} from '@mui/material';
import MainService from '../../services/MainService';
import * as XLSX from 'xlsx';
import { useLanguage } from '../../contexts/LanguageContext';
import { getPageHeading } from '../../locales/navigation';
import ClientListGrid, { useClientListGrid } from '../ui/ClientListGrid';

const checkboxSx = {
  color: '#46955A',
  '&.Mui-checked': {
    color: '#46955A',
  },
};

const PAYMENT_COLUMNS = [
  { field: 'refno', title: 'Ref No', widthClass: 'w-[16%]', hideable: true },
  { field: 'date', title: 'Date', widthClass: 'w-[14%]', hideable: true },
  { field: 'clientmoney', title: 'Client Money', widthClass: 'w-[16%]', hideable: true, align: 'right' },
  { field: 'status', title: 'Status', widthClass: 'w-[16%]', hideable: true },
  { field: 'employee', title: 'Employee', widthClass: 'w-[18%]', hideable: true },
  { field: 'createdby', title: 'Created By', widthClass: 'w-[16%]', hideable: true },
];

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return dateString;
  }
};

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '0.00';
  return parseFloat(amount).toFixed(2);
};

const getCellValue = (row, field) => {
  switch (field) {
    case 'refno':
      return row.RefNo || 'N/A';
    case 'date':
      return formatDate(row.Date);
    case 'clientmoney':
      return formatCurrency(row.ClientMoney);
    case 'status':
      return row.Status || 'N/A';
    case 'employee':
      return row.Employee?.Name || 'N/A';
    case 'createdby':
      return row.Createdby?.UserName || 'N/A';
    default:
      return row[field];
  }
};

const PaymentApprovals = () => {
  const { language } = useLanguage();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPayments, setSelectedPayments] = useState(new Set());

  const filteredPayments = useMemo(() => {
    if (!searchTerm.trim()) return payments;
    const keyword = searchTerm.toLowerCase();
    return payments.filter((payment) =>
      (payment.RefNo && payment.RefNo.toLowerCase().includes(keyword)) ||
      (payment.Employee?.Name && payment.Employee.Name.toLowerCase().includes(keyword)) ||
      (payment.Createdby?.UserName && payment.Createdby.UserName.toLowerCase().includes(keyword)) ||
      (payment.Status && payment.Status.toLowerCase().includes(keyword))
    );
  }, [searchTerm, payments]);

  const columns = useMemo(() => PAYMENT_COLUMNS, []);
  const grid = useClientListGrid({ columns, rows: filteredPayments, getCellValue });
  const pagedRows = useMemo(
    () => grid.displayGroups.flatMap((group) => group.rows),
    [grid.displayGroups]
  );
  const selectAll = pagedRows.length > 0 && pagedRows.every((p) => selectedPayments.has(p.Id));

  const clientMoneySum = useMemo(
    () => grid.processedRows.reduce((sum, p) => sum + parseFloat(p.ClientMoney || 0), 0),
    [grid.processedRows]
  );

  const summaryTotals = useMemo(() => {
    const selected = filteredPayments.filter((p) => selectedPayments.has(p.Id));
    return {
      TotalClientMoney: selected.reduce((sum, payment) => sum + parseFloat(payment.ClientMoney || 0), 0),
    };
  }, [selectedPayments, filteredPayments]);

  useEffect(() => {
    loadPayments();
  }, []);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadPayments = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await MainService.ClientPaymentsForApprovals();
      if (response && Array.isArray(response)) {
        setPayments(response);
      } else {
        setPayments([]);
      }
    } catch (err) {
      console.error('Error loading payments:', err);
      setError('Failed to load payments');
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (checked) => {
    const pageIds = pagedRows.map((p) => p.Id);
    setSelectedPayments((prev) => {
      const next = new Set(prev);
      if (checked) {
        pageIds.forEach((id) => next.add(id));
      } else {
        pageIds.forEach((id) => next.delete(id));
      }
      return next;
    });
  };

  const handleSelectPayment = (paymentId, checked) => {
    setSelectedPayments((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(paymentId);
      } else {
        next.delete(paymentId);
      }
      return next;
    });
  };

  const handleApprovePayments = async () => {
    if (selectedPayments.size === 0) {
      setError('Please select at least one payment to approve');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const paymentArray = Array.from(selectedPayments).map((id) => ({ Id: id }));
      const response = await MainService.ApproveClientPayment(paymentArray);

      if (response === 'Success' || response === true) {
        setSuccess(`Successfully approved ${selectedPayments.size} payment(s)`);
        setSelectedPayments(new Set());
        loadPayments();
      } else {
        setError(response || 'Failed to approve payments');
      }
    } catch (err) {
      console.error('Error approving payments:', err);
      setError('Failed to approve payments');
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    if (grid.processedRows.length === 0) {
      setError('No data to export');
      return;
    }

    const dataToExport = grid.processedRows.map((payment) => ({
      'Ref No': payment.RefNo || '',
      'Date': formatDate(payment.Date),
      'Client Money': formatCurrency(payment.ClientMoney),
      'Status': payment.Status || '',
      'Employee': payment.Employee?.Name || '',
      'Created By': payment.Createdby?.UserName || '',
      'Clients': payment.ClientsNames?.join(', ') || '',
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Payments');
    XLSX.writeFile(wb, `PaymentApprovals_${new Date().toISOString().split('T')[0]}.xlsx`);
    setSuccess('Data exported to Excel successfully');
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
          <div className="flex items-center">
            <List className="w-6 h-6 text-[#46955A] mr-2" />
            <div>
              <h1 className="text-xl font-semibold text-gray-900">{getPageHeading(language, 'paymentApprovalsList')}</h1>
              <p className="text-sm text-gray-500 mt-1">Review and approve pending client payments</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <TextField
              size="small"
              placeholder="Search payments..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search className="w-4 h-4 text-gray-400" />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: '300px',
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#46955A',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#46955A',
                  },
                },
              }}
            />
            <Tooltip title="Export to Excel">
              <IconButton
                onClick={exportToExcel}
                disabled={grid.processedRows.length === 0}
                sx={{
                  color: '#46955A',
                  '&:hover': { backgroundColor: '#f0f9f4' },
                  '&:disabled': { color: '#9CA3AF' }
                }}
              >
                <Download className="w-5 h-5" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh">
              <IconButton
                onClick={loadPayments}
                disabled={loading}
                sx={{
                  color: '#46955A',
                  '&:hover': { backgroundColor: '#f0f9f4' }
                }}
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </IconButton>
            </Tooltip>
          </div>
        </div>

        <ClientListGrid
          columns={columns}
          grid={grid}
          loading={loading}
          loadingText="Loading payments..."
          emptyText={searchTerm ? 'No payments found matching your search' : 'No payments pending approval'}
          minWidth="1100px"
          rowKey={(row) => row.Id}
          getRowClassName={(row) => (selectedPayments.has(row.Id) ? 'bg-[#f0f9f4]' : '')}
          leadingHeader={
            <th className="w-[4%] px-4 py-2">
              <Checkbox
                checked={selectAll}
                onChange={(e) => handleSelectAll(e.target.checked)}
                size="small"
                sx={checkboxSx}
              />
            </th>
          }
          renderLeadingCell={(row) => (
            <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
              <Checkbox
                checked={selectedPayments.has(row.Id)}
                onChange={(e) => handleSelectPayment(row.Id, e.target.checked)}
                size="small"
                sx={checkboxSx}
              />
            </td>
          )}
          renderCell={(row, column) => {
            if (column.field === 'clientmoney') {
              return <span className="font-medium text-emerald-600">{getCellValue(row, column.field)}</span>;
            }
            if (column.field === 'status') {
              const isDone = row.Status === 'STATUS DONE';
              return (
                <span
                  className="px-2 py-1 text-xs font-medium rounded"
                  style={{
                    backgroundColor: isDone ? '#D1FAE5' : '#FEE2E2',
                    color: isDone ? '#065F46' : '#991B1B',
                  }}
                >
                  {row.Status || 'N/A'}
                </span>
              );
            }
            if (column.field === 'refno') {
              return <span className="font-medium text-gray-900">{getCellValue(row, column.field)}</span>;
            }
            return getCellValue(row, column.field);
          }}
          footerExtra={
            grid.totalCount > 0 ? (
              <tr className="border-t-2 border-gray-200 bg-gray-50 font-semibold">
                <td colSpan={4} className="px-4 py-2 text-right">Total:</td>
                <td className="px-4 py-2 text-right">{formatCurrency(clientMoneySum)}</td>
                <td colSpan={Math.max(grid.colSpan - 4, 0)}></td>
              </tr>
            ) : null
          }
        />

        {grid.totalCount > 0 && (
          <div className="bg-blue-50 rounded-lg border border-blue-200 p-4 mt-4">
            <div className="text-center">
              <p className="text-sm font-semibold text-gray-700 mb-2">All Payments Summary</p>
              <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-gray-600">
                <span><strong>Total Client Money:</strong> {formatCurrency(clientMoneySum)}</span>
                <span>|</span>
                <span><strong>Total Payments:</strong> {grid.totalCount}</span>
              </div>
            </div>
          </div>
        )}

        {selectedPayments.size > 0 && (
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 mt-4">
            <div className="text-center">
              <p className="text-sm font-semibold text-gray-700 mb-2">Selected Payments Summary</p>
              <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-gray-600">
                <span><strong>Total Client Money:</strong> {formatCurrency(summaryTotals.TotalClientMoney)}</span>
                <span>|</span>
                <span><strong>Selected Count:</strong> {selectedPayments.size}</span>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-center gap-3 mt-4">
          <Button
            variant="outlined"
            startIcon={<RefreshCw className="w-4 h-4" />}
            onClick={loadPayments}
            disabled={loading}
            sx={{
              borderColor: '#46955A',
              color: '#46955A',
              '&:hover': { borderColor: '#3d7d4f', backgroundColor: '#f0f9f4' },
              '&:disabled': { borderColor: '#9CA3AF', color: '#9CA3AF' }
            }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <Check className="w-4 h-4" />}
            onClick={handleApprovePayments}
            disabled={loading || selectedPayments.size === 0}
            sx={{
              backgroundColor: '#46955A',
              '&:hover': { backgroundColor: '#3d7d4f' },
              '&:disabled': { backgroundColor: '#9CA3AF' }
            }}
          >
            Approve Payments ({selectedPayments.size})
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PaymentApprovals;
